import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Mail } from 'lucide-react';
import { authVerifyOtp, authResendOtp } from '@/lib/api';
import { toast } from 'sonner';

const OTP_LENGTH = 6;
const OTP_EXPIRY_SECONDS = 5 * 60;

export default function OtpVerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const email = location.state?.email || '';
  const from = location.state?.from || 'signup';

  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(OTP_EXPIRY_SECONDS);
  const inputRefs = useRef([]);

  // Redirect if no email in state
  useEffect(() => {
    if (!email) navigate('/signup', { replace: true });
  }, [email, navigate]);

  // Countdown timer
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const maskEmail = (e) => {
    const [local, domain] = e.split('@');
    return `${local[0]}${'*'.repeat(Math.max(local.length - 2, 1))}${local.slice(-1)}@${domain}`;
  };

  const handleDigitChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...digits];
    next[index] = value.slice(-1);
    setDigits(next);
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    const next = [...digits];
    pasted.split('').forEach((ch, i) => { next[i] = ch; });
    setDigits(next);
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIdx]?.focus();
  };

  const handleVerify = async () => {
    const otp = digits.join('');
    if (otp.length < OTP_LENGTH) { toast.error('Please enter the complete 6-digit OTP'); return; }
    setLoading(true);
    try {
      const res = await authVerifyOtp(email, otp);
      login(res.data);
      toast.success('Account created successfully!');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP. Please try again.');
      setDigits(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await authResendOtp(email);
      setSecondsLeft(OTP_EXPIRY_SECONDS);
      setDigits(Array(OTP_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
      toast.success('A new OTP has been sent to your email');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-[400px]">
        <div className="flex items-center gap-2.5">
          <img src="/logo192.png" alt="Flowlancer" className="w-8 h-8 rounded-md" />
          <span className="font-display text-subhead text-ink">FlowLancer</span>
        </div>

        <h1 className="mt-10 font-display text-display-l text-ink">Check your email</h1>
        <p className="mt-3 flex items-center gap-2 text-body text-ink-muted">
          <Mail className="w-4 h-4 shrink-0" strokeWidth={1.5} />
          We sent a 6-digit code to
        </p>
        <p className="mt-1 text-body text-ink">{maskEmail(email)}</p>

        <div className="mt-10 flex gap-2" onPaste={handlePaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputRefs.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => handleDigitChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              aria-label={`Digit ${i + 1}`}
              className="h-13 w-full rounded-md border border-line-strong bg-surface text-center text-subhead tabular-nums text-ink transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-paper"
              style={{ height: '52px' }}
            />
          ))}
        </div>

        <Button
          onClick={handleVerify}
          disabled={loading || digits.join('').length < OTP_LENGTH}
          className="mt-6 w-full h-11"
        >
          {loading ? 'Verifying…' : 'Verify & create account'}
        </Button>

        <div className="mt-6">
          {secondsLeft > 0 ? (
            <p className="text-small text-ink-muted">
              Code expires in{' '}
              <span className={`tabular-nums ${secondsLeft <= 30 ? 'text-status-overdue' : 'text-ink'}`}>
                {formatTime(secondsLeft)}
              </span>
            </p>
          ) : (
            <div className="space-y-3">
              <p className="text-small text-status-overdue">This code has expired.</p>
              <Button variant="outline" onClick={handleResend} disabled={resending}>
                {resending ? 'Sending…' : 'Resend code'}
              </Button>
            </div>
          )}
        </div>

        {secondsLeft > 0 && (
          <p className="mt-4 text-small text-ink-subtle">
            Didn't receive it?{' '}
            <button
              onClick={handleResend}
              disabled={resending || secondsLeft > 0}
              className="text-ink-muted hover:text-forest disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Resend
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
